import type { ActionType } from "typesafe-actions";
import { createReducer } from "typesafe-actions";

import { resetFilteringStateAction } from "app/pages/sales-intelligence/modules/advanced-search-filtering/store/action-creators";
import { contactsTableFiltersChangedAction } from "app/pages/sales-intelligence/modules/company-analysis/store/contacts/action-creators";

import { initialActionsPanelFilters } from "../constants/initialState";
import { CONTACTS_SEARCH_TABLE_INITIAL_PAGE, CONTACTS_SEARCH_TABLE_PAGE_SIZE } from "../constants/table";
import { IDLE_PHASE } from "../helpers/exportFlow";
import type { ContactExportPreview, ContactsSearchStateType } from "../types/common";

import * as ac from "./action-creators";

const DEFAULT_CONTACT_COST = 1;

const INITIAL_CONTACTS_SEARCH_STATE: ContactsSearchStateType = {
    searchResults: {
        rows: [],
        totalCount: undefined,
        revealedCount: undefined,
        notRevealedCount: undefined,
        excludedBySignalsCount: undefined,
    },
    areSearchResultsFetching: false,
    isResultsTotalCountBeingFetched: false,
    tableFilters: {
        page: CONTACTS_SEARCH_TABLE_INITIAL_PAGE,
        pageSize: CONTACTS_SEARCH_TABLE_PAGE_SIZE,
        searchText: "",
    },
    actionsPanelFilters: { ...initialActionsPanelFilters },
    selectedTableRows: [],
    isRowsSelectionEnabled: false,
    isMultipleRevealInProgress: false,
    quota: null,
    cost: DEFAULT_CONTACT_COST,
    isQuotaBeingFetched: true,
    revealLimitReachedModal: {
        isOpened: false,
        lastValidSelection: [],
    },
    contactIssueModal: null,
    isExportingToExcel: false,
    exportToExcelError: undefined,
    exportVariant: undefined,
    exportToCrm: {
        isFetchingPreview: false,
        contactsPreview: [],
        crm: undefined,
        as: undefined,
    },
    isContactUsHookUsed: false,
    isActionPanelActivated: false,
    requestedContactsForExport: [],
    pendingRevealInfo: null,
    exportFlowPhase: IDLE_PHASE,
    exportFlowOrigin: null,
};

const contactsSearchReducer = createReducer<
    ContactsSearchStateType,
    ActionType<typeof ac | typeof contactsTableFiltersChangedAction> | ReturnType<typeof resetFilteringStateAction>
>(INITIAL_CONTACTS_SEARCH_STATE)
    .handleAction(
        ac.contactsSearchResultsFetchedAsync.request,
        (state): ContactsSearchStateType => ({
            ...state,
            areSearchResultsFetching: true,
            searchResults: {
                ...state.searchResults,
                rows: INITIAL_CONTACTS_SEARCH_STATE.searchResults.rows,
                revealedCount: INITIAL_CONTACTS_SEARCH_STATE.searchResults.revealedCount,
                notRevealedCount: INITIAL_CONTACTS_SEARCH_STATE.searchResults.notRevealedCount,
            },
            searchResultsFetchError: INITIAL_CONTACTS_SEARCH_STATE.searchResultsFetchError,
        }),
    )
    .handleAction(
        ac.contactsSearchResultsFetchedAsync.success,
        (state, { payload }): ContactsSearchStateType => ({
            ...state,
            searchResults: {
                ...state.searchResults,
                ...payload,
            },
            areSearchResultsFetching: false,
        }),
    )
    .handleAction(
        ac.contactsSearchResultsFetchedAsync.failure,
        (state, { payload }): ContactsSearchStateType => ({
            ...state,
            areSearchResultsFetching: false,
            searchResultsFetchError: payload,
        }),
    )
    .handleAction(
        ac.contactsSearchResultsWithTotalCountFetchedAsync.request,
        (state): ContactsSearchStateType => ({
            ...state,
            areSearchResultsFetching: true,
            isResultsTotalCountBeingFetched: true,
            searchResults: {
                ...INITIAL_CONTACTS_SEARCH_STATE.searchResults,
            },
            searchResultsFetchError: INITIAL_CONTACTS_SEARCH_STATE.searchResultsFetchError,
        }),
    )
    .handleAction(
        ac.contactsSearchResultsWithTotalCountFetchedAsync.success,
        (state, { payload }): ContactsSearchStateType => ({
            ...state,
            searchResults: {
                ...state.searchResults,
                ...payload,
            },
            areSearchResultsFetching: false,
            isResultsTotalCountBeingFetched: false,
        }),
    )
    .handleAction(
        ac.contactsSearchResultsWithTotalCountFetchedAsync.failure,
        (state, { payload }): ContactsSearchStateType => ({
            ...state,
            areSearchResultsFetching: false,
            isResultsTotalCountBeingFetched: false,
            searchResultsFetchError: payload,
        }),
    )
    .handleAction(
        ac.searchResultsClearedAction,
        (state, { payload }): ContactsSearchStateType => ({
            ...state,
            areSearchResultsFetching:
                payload?.areResultsFetching ?? INITIAL_CONTACTS_SEARCH_STATE.areSearchResultsFetching,
            searchResults: INITIAL_CONTACTS_SEARCH_STATE.searchResults,
        }),
    )
    .handleAction(
        ac.contactsSearchResultsTotalCountFetchedAsync.request,
        (state): ContactsSearchStateType => ({
            ...state,
            searchResults: {
                ...state.searchResults,
                excludedBySignalsCount: INITIAL_CONTACTS_SEARCH_STATE.searchResults.excludedBySignalsCount,
                totalCount: INITIAL_CONTACTS_SEARCH_STATE.searchResults.totalCount,
            },
            isResultsTotalCountBeingFetched: true,
        }),
    )
    .handleAction(
        ac.contactsSearchResultsTotalCountFetchedAsync.success,
        (state, { payload }): ContactsSearchStateType => ({
            ...state,
            searchResults: {
                ...state.searchResults,
                totalCount: payload.totalCount,
                excludedBySignalsCount: payload.excludedBySignalsCount,
            },
            isResultsTotalCountBeingFetched: false,
        }),
    )
    .handleAction(
        ac.contactsSearchResultsTotalCountFetchedAsync.failure,
        (state): ContactsSearchStateType => ({
            ...state,
            isResultsTotalCountBeingFetched: false,
        }),
    )
    .handleAction(
        ac.multipleContactsWereRevealedAsync.request,
        (state): ContactsSearchStateType => ({
            ...state,
            isMultipleRevealInProgress: true,
        }),
    )
    .handleAction(ac.multipleContactsWereRevealedAsync.success, (state, { payload }): ContactsSearchStateType => {
        const newState = {
            ...state,
            isMultipleRevealInProgress: false,
        };

        return newState;
    })
    .handleAction(
        ac.multipleContactsWereRevealedAsync.failure,
        (state): ContactsSearchStateType => ({
            ...state,
            isMultipleRevealInProgress: false,
        }),
    )
    .handleAction(
        ac.actionsPanelFiltersChangedAction,
        (state, { payload }): ContactsSearchStateType => ({
            ...state,
            actionsPanelFilters: {
                ...state.actionsPanelFilters,
                ...payload,
            },
        }),
    )
    .handleAction(
        ac.toggleIncludeOnlyNotRevealed,
        (state, { payload }): ContactsSearchStateType => ({
            ...state,
            actionsPanelFilters: {
                ...state.actionsPanelFilters,
                includeOnlyNotRevealed: payload,
                includeOnlyRevealed: payload ? false : state.actionsPanelFilters.includeOnlyRevealed,
            },
        }),
    )
    .handleAction(
        ac.tableFiltersChangedAction,
        (state, { payload }): ContactsSearchStateType => ({
            ...state,
            tableFilters: {
                ...state.tableFilters,
                ...payload,
            },
            actionsPanelFilters: {
                ...state.actionsPanelFilters,
                page: typeof state.actionsPanelFilters.page === "undefined" ? undefined : payload.page,
            },
        }),
    )
    .handleAction(
        ac.tableSortingChangedAction,
        (state, { payload }): ContactsSearchStateType => ({
            ...state,
            sortBy: payload,
        }),
    )
    .handleAction(contactsTableFiltersChangedAction, (state, { payload }) => ({
        ...state,
        actionsPanelFilters: {
            ...state.actionsPanelFilters,
            page: typeof state.actionsPanelFilters.page === "undefined" ? undefined : payload.page,
        },
    }))
    .handleAction(
        ac.tableFiltersClearedAction,
        (state): ContactsSearchStateType => ({
            ...state,
            tableFilters: INITIAL_CONTACTS_SEARCH_STATE.tableFilters,
        }),
    )
    .handleAction(
        ac.tableRowsSelectionToggledAction,
        (state, { payload }): ContactsSearchStateType => ({
            ...state,
            isRowsSelectionEnabled: payload,
        }),
    )
    .handleAction(
        ac.tableRowsSelectionChangedAction,
        (state, { payload }): ContactsSearchStateType => ({
            ...state,
            selectedTableRows: payload,
        }),
    )
    .handleAction(
        ac.exportedToExcelAsync.request,
        (state): ContactsSearchStateType => ({
            ...state,
            isExportingToExcel: true,
            exportToExcelError: INITIAL_CONTACTS_SEARCH_STATE.exportToExcelError,
        }),
    )
    .handleAction(
        ac.exportedToExcelAsync.success,
        (state): ContactsSearchStateType => ({
            ...state,
            isExportingToExcel: false,
            exportVariant: undefined,
        }),
    )
    .handleAction(
        ac.exportedToExcelAsync.failure,
        (state, { payload }): ContactsSearchStateType => ({
            ...state,
            isExportingToExcel: false,
            exportToExcelError: payload,
            exportVariant: undefined,
        }),
    )
    .handleAction(ac.usedQuotaUpdatedAction, (state, { payload }) => {
        if (!state.quota) {
            return state;
        }

        return {
            ...state,
            quota: {
                ...state.quota,
                used: state.quota.used + payload,
                remaining: state.quota.remaining - payload,
            },
        };
    })
    .handleAction(ac.revealLimitReachedModalToggledAction, (state, { payload }) => ({
        ...state,
        revealLimitReachedModal: payload,
    }))
    .handleAction(ac.contactIssueModalToggledAction, (state, { payload }) => ({
        ...state,
        contactIssueModal: payload,
    }))
    .handleAction(ac.contactReportedAction, (state, { payload: contactId }) => ({
        ...state,
        searchResults: {
            ...state.searchResults,
            rows: state.searchResults.rows.map((contact) =>
                contact.id === contactId ? { ...contact, hasIssues: true } : contact,
            ),
        },
    }))
    .handleAction(
        ac.userContactsQuotaFetchedAsync.request,
        (state): ContactsSearchStateType => ({
            ...state,
            isQuotaBeingFetched: true,
        }),
    )
    .handleAction(
        ac.userContactsQuotaFetchedAsync.success,
        (state, { payload }): ContactsSearchStateType => ({
            ...state,
            isQuotaBeingFetched: false,
            ...payload,
        }),
    )
    .handleAction(
        ac.userContactsQuotaFetchedAsync.failure,
        (state): ContactsSearchStateType => ({
            ...state,
            isQuotaBeingFetched: false,
        }),
    )
    .handleAction(ac.initExportToExcel, (state) => ({ ...state, exportVariant: "excel" }))
    .handleAction(ac.initExportToCrm, (state, { payload }) => ({
        ...state,
        exportVariant: "crm",
        exportToCrm: { ...state.exportToCrm, crm: payload.crm, as: payload.as },
    }))
    .handleAction(ac.fetchExportToCrmPreviewAsync.request, (state) => ({
        ...state,
        exportToCrm: { ...state.exportToCrm, isFetchingPreview: true },
    }))
    .handleAction(ac.fetchExportToCrmPreviewAsync.success, (state, { payload }) => ({
        ...state,
        exportToCrm: {
            ...state.exportToCrm,
            isFetchingPreview: false,
            contactsPreview: payload,
        },
    }))
    .handleAction(ac.fetchExportToCrmPreviewAsync.failure, (state) => ({
        ...state,
        exportToCrm: { ...state.exportToCrm, isFetchingPreview: false },
    }))
    .handleAction(ac.contactExportPreviewChanged, (state, { payload }: { payload: ContactExportPreview }) => {
        const contactsPreview = state.exportToCrm.contactsPreview.map((contact) => {
            return payload.contactId === contact.contactId ? payload : contact;
        });

        return { ...state, exportToCrm: { ...state.exportToCrm, contactsPreview } };
    })
    .handleAction(ac.contactExportPreviewDeleted, (state, { payload }) => {
        const contactsPreview = state.exportToCrm.contactsPreview.filter((contact) => contact.contactId !== payload);

        return { ...state, exportToCrm: { ...state.exportToCrm, contactsPreview } };
    })
    .handleAction(ac.toggleContactUsHook, (state) => {
        return { ...state, isContactUsHookUsed: true };
    })
    .handleAction(ac.setIsActionPanelActivated, (state, { payload }) => {
        return { ...state, isActionPanelActivated: payload };
    })
    .handleAction(ac.resetExportToCrm, (state) => ({
        ...state,
        exportVariant: undefined,
        exportToCrm: INITIAL_CONTACTS_SEARCH_STATE.exportToCrm,
        requestedContactsForExport: INITIAL_CONTACTS_SEARCH_STATE.requestedContactsForExport,
        pendingRevealInfo: null,
    }))
    .handleAction(ac.setRequestedContactsForExport, (state, { payload }) => ({
        ...state,
        requestedContactsForExport: payload,
    }))
    .handleAction(ac.resetRequestedContactsForExport, (state) => ({
        ...state,
        requestedContactsForExport: INITIAL_CONTACTS_SEARCH_STATE.requestedContactsForExport,
    }))
    .handleAction(ac.setPendingRevealInfo, (state, { payload }) => ({
        ...state,
        pendingRevealInfo: payload,
    }))
    .handleAction(ac.clearPendingRevealInfo, (state) => ({
        ...state,
        pendingRevealInfo: null,
    }))
    .handleAction(ac.setExportFlowPhase, (state, { payload }) => ({
        ...state,
        exportFlowPhase: payload,
    }))
    .handleAction(ac.setExportFlowOrigin, (state, { payload }) => ({
        ...state,
        exportFlowOrigin: payload,
    }))
    .handleAction(
        resetFilteringStateAction,
        (state): ContactsSearchStateType => ({
            ...state,
            tableFilters: INITIAL_CONTACTS_SEARCH_STATE.tableFilters,
        }),
    );

export default contactsSearchReducer;
